import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutGrid, CalendarCheck, TrendingUp, CreditCard, LogOut, Menu, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { WordMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutGrid, end: true },
  { to: "/dashboard/bookings", label: "My Bookings", icon: CalendarCheck },
  { to: "/dashboard/progress", label: "Progress & PRs", icon: TrendingUp },
  { to: "/dashboard/membership", label: "Membership", icon: CreditCard },
];

const ADMIN_NAV = { to: "/dashboard/admin", label: "Content Editor", icon: ShieldCheck };

function NavItems({ isAdmin }) {
  const items = isAdmin ? [...NAV, ADMIN_NAV] : NAV;
  return (
    <nav className="flex flex-col gap-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            `flex items-center gap-3 px-6 py-3 text-sm uppercase tracking-wider transition-colors ${
              isActive ? "bg-gold text-void font-semibold" : "text-cream/70 hover:bg-line/40 hover:text-gold"
            }`
          }
          data-testid={`dashboard-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <item.icon className="h-4 w-4" /> {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-void flex flex-col lg:flex-row">
      <aside className="hidden lg:flex flex-col w-64 border-r border-line bg-surface shrink-0 safe-top">
        <div className="flex items-center gap-2.5 px-6 py-6 border-b border-line">
          <WordMark className="h-14 w-14" />
        </div>
        <div className="flex-1 py-6">
          <NavItems isAdmin={user?.role === "admin"} />
        </div>
        <div className="p-6 border-t border-line">
          <p className="text-cream text-sm truncate">{user?.name}</p>
          <p className="text-smoke text-xs truncate mb-3">{user?.email}</p>
          <button onClick={handleLogout} className="flex items-center gap-2 text-smoke hover:text-gold text-sm" data-testid="dashboard-logout-button">
            <LogOut className="h-4 w-4" /> Log Out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden flex items-center justify-between px-4 h-16 border-b border-line bg-surface safe-top">
          <div className="flex items-center gap-2">
            <WordMark className="h-11 w-11" />
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <button className="text-cream p-2" aria-label="Open dashboard menu" data-testid="dashboard-mobile-menu-trigger">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-void border-l border-gold/20 w-[80vw] safe-top safe-bottom p-0">
              <SheetTitle className="sr-only">Dashboard Menu</SheetTitle>
              <div className="flex items-center gap-2.5 px-6 py-6 border-b border-line">
                <WordMark className="h-14 w-14" />
              </div>
              <SheetClose asChild>
                <div className="py-4">
                  <NavItems isAdmin={user?.role === "admin"} />
                </div>
              </SheetClose>
              <div className="p-6 border-t border-line mt-4">
                <button onClick={handleLogout} className="flex items-center gap-2 text-smoke hover:text-gold text-sm" data-testid="dashboard-mobile-logout-button">
                  <LogOut className="h-4 w-4" /> Log Out
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-10 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
